import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';
import { useMergeRefs } from '@floating-ui/react';
import type { UseComboboxReturnValue } from 'downshift';
import { forwardRef, useEffect } from 'react';

import { TypeaheadComboboxControls } from './TypeaheadComboboxControls';
import { useTypeaheadComboboxContext } from './hooks';
import type { Theme } from '../../theme';
import { useDesignSystemTheme } from '../Hooks';
import type { InputProps } from '../Input';
import { Input } from '../Input';

export interface TypeaheadComboboxInputProps<T> extends InputProps {
  comboboxState: UseComboboxReturnValue<T>;
  allowClear?: boolean;
  /* Form libraries like RHF and AntD should pass in an onChange here to allow the clear button to update the value */
  formOnChange?: (value: T) => void;
}

const getContainerStyles = (): SerializedStyles => {
  return css({
    display: 'flex',
    position: 'relative',
  });
};

const getInputStyles = (theme: Theme): SerializedStyles =>
  css({
    paddingRight: 52,
    width: '100%',
    minWidth: 72,

    '&:disabled': {
      border: 'none',
      backgroundColor: theme.colors.actionDisabledBackground,
      color: theme.colors.actionDisabledText,
    },

    '&:not(:disabled)': {
      backgroundColor: 'transparent',
    },
  });

export const TypeaheadComboboxInput = forwardRef<HTMLDivElement | null, TypeaheadComboboxInputProps<any>>(
  ({ comboboxState, allowClear = true, formOnChange, onClick, ...restProps }, ref) => {
    const { isInsideTypeaheadCombobox, floatingUiRefs, setInputWidth, inputWidth } = useTypeaheadComboboxContext();
    if (!isInsideTypeaheadCombobox) {
      throw new Error('`TypeaheadComboboxInput` must be used within `TypeaheadCombobox`');
    }

    const { getInputProps, getToggleButtonProps, toggleMenu, inputValue, setInputValue, reset } = comboboxState;
    const { ref: downshiftRef, ...downshiftProps } = getInputProps({}, { suppressRefError: true });
    const mergedRef = useMergeRefs([ref, downshiftRef]);
    const { theme } = useDesignSystemTheme();

    const handleClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      onClick?.(e);
      toggleMenu();
    };

    const handleClear = () => {
      setInputValue('');
      reset();
      formOnChange?.(null);
    };

    // Gets the width of the input and sets it inside the context for rendering the dropdown when `matchTriggerWidth` is true on the menu
    useEffect(() => {
      // Use the DOM reference of the TypeaheadComboboxInput container div to get the width of the input
      if (floatingUiRefs?.domReference) {
        const width = floatingUiRefs.domReference.current?.getBoundingClientRect().width ?? 0;
        // Only update context width when the input width updated
        if (width !== inputWidth) {
          setInputWidth?.(width);
        }
      }
    }, [floatingUiRefs?.domReference, setInputWidth, inputWidth]);

    return (
      <div ref={floatingUiRefs?.setReference} css={getContainerStyles()} className={restProps.className}>
        <Input ref={mergedRef} {...downshiftProps} onClick={handleClick} css={getInputStyles(theme)} {...restProps} />

        <TypeaheadComboboxControls
          getDownshiftToggleButtonProps={getToggleButtonProps}
          showClearSelectionButton={allowClear && Boolean(inputValue) && !restProps.disabled}
          handleClear={handleClear}
          disabled={restProps.disabled}
        />
      </div>
    );
  },
);
